import { motion, useReducedMotion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { fadeUp } from '../lib/motion'
import { useAuth } from '../hooks/useAuth'
import { useT } from '../i18n/t'

import { HeroPassportStampsCard } from '../components/landing/HeroPassportStampsCard'
import heroBackgroundDesktop from '../assets/optimized/hero-1600.webp'
import heroBackgroundMobile from '../assets/optimized/hero-960.webp'

/**
 * Hero strony głównej — wariant "wystartował 2026":
 * — pełnoekranowe tło z panoramą Sudetów,
 * — podgląd kartki z pieczątkami jak w panelu uczestnika,
 * — minimalna typografia + link „Dołączam!".
 */
export function HeroBlockSection() {
  const reduceMotion = useReducedMotion()
  const { t } = useT()
  const { isLoggedIn } = useAuth()

  return (
    <section id="start" className="relative isolate overflow-hidden bg-[#010218] text-white">
      <motion.div className="absolute inset-0 -z-10">
        <picture>
          <source media="(max-width: 900px)" srcSet={heroBackgroundMobile} />
          <img
            src={heroBackgroundDesktop}
            alt=""
            aria-hidden="true"
            className="h-full w-full object-cover"
            decoding="async"
            fetchPriority="high"
          />
        </picture>
        <div
          aria-hidden="true"
          className="absolute inset-0 bg-gradient-to-b from-[#010218]/80 from-0% via-[#010218]/45 via-[35%] to-[#010218] to-[88%]"
        />
      </motion.div>

      <div className="container-x relative">
        <div className="content-wide grid grid-cols-1 items-center gap-8 pt-24 pb-14 md:grid-cols-[1.25fr_1fr] md:gap-12 md:pt-40 md:pb-20 lg:pb-24">
          {/* Mobile: kartka z pieczątkami nad tekstem. */}
          <motion.div
            initial={reduceMotion ? false : { opacity: 0, y: 12 }}
            animate={reduceMotion ? undefined : { opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1, ease: 'easeOut' }}
            className="relative z-0 -mb-4 mt-2 w-full max-w-[300px] place-self-end md:hidden"
          >
            <HeroPassportStampsCard />
          </motion.div>

          <motion.div
            initial={reduceMotion ? false : 'hidden'}
            animate={reduceMotion ? undefined : 'visible'}
            variants={fadeUp}
            transition={{ duration: 0.7, ease: 'easeOut' }}
            className="relative z-10"
          >
            <div className="flex items-center gap-3">
              <span
                aria-hidden
                className="block h-px w-10 bg-white/70 md:w-14"
              />
              <p className="text-[12px] font-semibold uppercase tracking-[0.18em] text-white/85 md:text-[13px]">
                {t('landing.heroLaunch.eyebrow')}
              </p>
            </div>

            <h1 className="display mt-4 text-[34px] font-normal leading-[1.05] text-white md:mt-6 md:text-[60px] md:leading-[1.02] lg:text-[68px]">
              {t('landing.heroLaunch.headline')}
            </h1>

            <p className="mt-7 md:mt-10">
              <Link
                to={isLoggedIn ? '/panel' : '/formularz'}
                className="inline-block text-[20px] font-extrabold text-white underline decoration-white/75 underline-offset-[6px] transition hover:decoration-white md:text-[22px]"
              >
                {isLoggedIn
                  ? t('landing.heroLaunch.form.alreadyLogged.cta')
                  : t('landing.heroLaunch.joinCta')}
              </Link>
            </p>
          </motion.div>

          <motion.div
            initial={reduceMotion ? false : { opacity: 0, y: 16, rotate: -2 }}
            animate={reduceMotion ? undefined : { opacity: 1, y: 0, rotate: 0 }}
            transition={{ duration: 0.9, delay: 0.2, ease: 'easeOut' }}
            className="relative mx-auto hidden w-full max-w-[400px] md:block lg:max-w-[420px]"
          >
            <HeroPassportStampsCard />
          </motion.div>
        </div>
      </div>

    </section>
  )
}
